import React, { useEffect, useContext, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Typography, CircularProgress, IconButton } from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { setActiveView } from '../../store/slices/uiSlice';
import { makeStyles } from '@material-ui/core/styles';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  AreaChart, Area, CartesianGrid, Legend, Cell,
} from 'recharts';
import DirectionsIcon from '@material-ui/icons/Directions';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty';
import EuroSymbolIcon from '@material-ui/icons/EuroSymbol';
import MapIcon from '@material-ui/icons/Map';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import WarningIcon from '@material-ui/icons/Warning';
import ErrorIcon from '@material-ui/icons/Error';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import PersonIcon from '@material-ui/icons/Person';
import SettingsIcon from '@material-ui/icons/Settings';
import { AuthContext } from '../../App';
import { selectVehicles } from '../../store/slices/vehiclesSlice';
import ManageFleetsDialog from '../Fleet/ManageFleetsDialog';
import MaintenanceDialog from '../Fleet/MaintenanceDialog';
import GeofenceDialog from '../Fleet/GeofenceDialog';
import BuildIcon from '@material-ui/icons/Build';
import GpsFixedIcon from '@material-ui/icons/GpsFixed';
import ManageDriversDialog from '../Sidebar/ManageDriversDialog';
import ManageVehiclesDialog from '../Sidebar/ManageVehiclesDialog';
import {
  fetchOverview, fetchMonthlyCosts, fetchCostsByDriver, fetchCostsByVehicle, fetchAlerts,
  selectOverview, selectMonthlyCosts, selectCostsByDriver, selectCostsByVehicle, selectAlerts, selectStatsLoading,
} from '../../store/slices/statsSlice';

const MONTH_NAMES_PT = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
function formatMonth(m) {
  const parts = (m || '').split('-');
  return MONTH_NAMES_PT[parseInt(parts[1], 10) - 1] || m;
}

const STATUS_COLORS = {
  draft: { pt: 'Rascunho', en: 'Draft', color: '#94a3b8' },
  planned: { pt: 'Planeada', en: 'Planned', color: '#3b82f6' },
  in_progress: { pt: 'Em Trânsito', en: 'In Transit', color: '#f59e0b' },
  completed: { pt: 'Concluída', en: 'Completed', color: '#22c55e' },
  cancelled: { pt: 'Cancelada', en: 'Cancelled', color: '#ef4444' },
};

/* ── Custom Recharts Tooltip ── */
const CustomTooltip = ({ active, payload, label, formatter }) => {
  if (!active || !payload) return null;
  return (
    <Box style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 14px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
      <Typography style={{ fontSize: '0.72rem', fontWeight: 600, color: '#0f172a', marginBottom: 4 }}>{label}</Typography>
      {payload.map((p, i) => (
        <Box key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Box style={{ width: 8, height: 8, borderRadius: '50%', background: p.color }} />
          <Typography style={{ fontSize: '0.68rem', color: '#64748b' }}>{p.name}: <b>{formatter ? formatter(p.value, p.name) : p.value}</b></Typography>
        </Box>
      ))}
    </Box>
  );
};

const useStyles = makeStyles(() => ({
  root: {
    flex: 1,
    overflow: 'auto',
    padding: 24,
    background: '#f8fafc',
  },
  header: {
    marginBottom: 24,
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: '#0f172a',
    letterSpacing: '-0.02em',
  },
  subtitle: {
    fontSize: '0.82rem',
    color: '#64748b',
    marginTop: 2,
  },
  /* ── Section header above each row ── */
  sectionHeader: {
    fontSize: '0.85rem',
    fontWeight: 700,
    color: '#0f172a',
    marginBottom: 10,
    paddingBottom: 6,
    borderBottom: '1.5px solid #e2e8f0',
  },
  kpiGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 16,
    marginBottom: 24,
    '@media (max-width: 900px)': {
      gridTemplateColumns: 'repeat(2, 1fr)',
    },
  },
  row2: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 16,
    marginBottom: 24,
    '@media (max-width: 900px)': {
      gridTemplateColumns: '1fr',
    },
  },
  card: {
    background: '#fff',
    borderRadius: 14,
    padding: 20,
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
  },
  sectionTitle: {
    fontSize: '0.88rem',
    fontWeight: 700,
    color: '#0f172a',
    marginBottom: 16,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  tableWrap: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    '& th': {
      textAlign: 'left',
      fontSize: '0.7rem',
      fontWeight: 700,
      color: '#64748b',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      padding: '8px 12px',
      borderBottom: '1px solid #e2e8f0',
      cursor: 'pointer',
      userSelect: 'none',
      whiteSpace: 'nowrap',
      '&:hover': {
        color: '#3b82f6',
      },
    },
    '& td': {
      fontSize: '0.78rem',
      color: '#334155',
      padding: '10px 12px',
      borderBottom: '1px solid #f1f5f9',
      whiteSpace: 'nowrap',
    },
    '& tbody tr:hover': {
      background: '#f8fafc',
    },
  },
  alertItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 12px',
    borderRadius: 10,
    marginBottom: 6,
    '&:hover': { background: '#f8fafc' },
  },
  alertIcon: {
    width: 32,
    height: 32,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  recentRouteItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 12px',
    borderRadius: 10,
    marginBottom: 4,
    '&:hover': { background: '#f8fafc' },
  },
  statusChip: {
    fontSize: '0.62rem',
    fontWeight: 700,
    padding: '2px 8px',
    borderRadius: 6,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  loadingWrap: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 300,
  },
  /* ── KPI card with hover + left accent ── */
  kpiCard: {
    background: '#fff',
    borderRadius: 14,
    padding: '16px 20px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    transition: 'transform 0.18s ease, box-shadow 0.18s ease',
    cursor: 'default',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 20px rgba(0,0,0,0.10)',
    },
  },
}));

function KpiCard({ icon: Icon, color, value, label, change, lang = 'pt' }) {
  const classes = useStyles();
  return (
    <Box className={classes.kpiCard} style={{ borderLeft: `3.5px solid ${color}` }}>
      <Box style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <Box style={{
          width: 40, height: 40, borderRadius: 10,
          background: `${color}15`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Icon style={{ fontSize: 20, color }} />
        </Box>
        <Box style={{ flex: 1 }}>
          <Typography style={{ fontSize: '1.4rem', fontWeight: 700, color: '#0f172a', lineHeight: 1.1 }}>{value}</Typography>
          <Typography style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: 2 }}>{label}</Typography>
        </Box>
      </Box>
      {change !== undefined && change !== null && (
        <Box style={{
          display: 'flex', alignItems: 'center', gap: 3, marginTop: 10,
          fontSize: '0.68rem', fontWeight: 600,
          color: change >= 0 ? '#22c55e' : '#ef4444',
        }}>
          {change >= 0
            ? <ArrowUpwardIcon style={{ fontSize: 13 }} />
            : <ArrowDownwardIcon style={{ fontSize: 13 }} />}
          {Math.abs(change).toFixed(0)}% {lang === 'pt' ? 'vs mês anterior' : 'vs previous month'}
        </Box>
      )}
    </Box>
  );
}

function SortableTable({ columns, data, defaultSort, lang = 'pt' }) {
  const [sortCol, setSortCol] = useState(defaultSort || columns[0]?.key);
  const [sortDir, setSortDir] = useState('desc');

  const sorted = useMemo(() => {
    if (!data || !data.length) return [];
    return [...data].sort((a, b) => {
      const va = a[sortCol];
      const vb = b[sortCol];
      if (typeof va === 'number' && typeof vb === 'number') {
        return sortDir === 'asc' ? va - vb : vb - va;
      }
      return sortDir === 'asc'
        ? String(va || '').localeCompare(String(vb || ''))
        : String(vb || '').localeCompare(String(va || ''));
    });
  }, [data, sortCol, sortDir]);

  const toggleSort = (key) => {
    if (sortCol === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(key);
      setSortDir('desc');
    }
  };

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr>
          {columns.map(c => (
            <th
              key={c.key}
              onClick={() => toggleSort(c.key)}
              style={{
                textAlign: 'left', fontSize: '0.7rem', fontWeight: 700,
                color: sortCol === c.key ? '#3b82f6' : '#64748b',
                textTransform: 'uppercase', letterSpacing: '0.05em', padding: '8px 12px',
                borderBottom: '2px solid #e2e8f0', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap',
                background: '#f8fafc',
              }}
            >
              {c.label} {sortCol === c.key ? (sortDir === 'asc' ? '\u2191' : '\u2193') : ''}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {sorted.map((row, i) => (
          <tr
            key={i}
            style={{
              borderBottom: '1px solid #f1f5f9',
              background: i % 2 === 0 ? '#fff' : '#f8fafc',
              transition: 'background 0.15s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#eff6ff'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = i % 2 === 0 ? '#fff' : '#f8fafc'; }}
          >
            {columns.map(c => (
              <td key={c.key} style={{
                fontSize: '0.78rem', color: '#334155', padding: '10px 12px', whiteSpace: 'nowrap',
              }}>
                {c.render ? c.render(row[c.key], row) : row[c.key]}
              </td>
            ))}
          </tr>
        ))}
        {sorted.length === 0 && (
          <tr>
            <td colSpan={columns.length} style={{ textAlign: 'center', padding: 20, color: '#94a3b8', fontSize: '0.78rem' }}>
              {lang === 'pt' ? 'Sem dados' : 'No data'}
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}

function DashboardView({ lang }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { token, clientSettings, modules } = useContext(AuthContext);
  // Custos de frota é agora um MÓDULO (era gateado pela variante). Fail-open
  // quando não há lista de módulos, igual ao ModuleGate.
  const hasFleetCosts = !modules || modules.length === 0 || modules.includes('fleet_costs');
  // Move Express (externalId '8028') vê um dashboard reduzido — escondemos
  // widgets que não fazem parte da operação de combustível deles.
  // NOTA: o DashboardView genérico NÃO é mostrado à variante fuel (o AppLayout
  // encaminha essa para o FuelOpsDashboard). Removida a lógica isMoveExpress que
  // vivia aqui — era código morto (constante-false ao renderizar).
  // Símbolo da moeda das preferências do cliente. Default EUR. Aplicado nos
  // KPIs financeiros do Dashboard (Receita, Custo, Margem).
  const currencySymbol = (
    { EUR: '€', AOA: 'Kz', USD: '$', GBP: '£', NAD: 'N$', ZAR: 'R' }
    [clientSettings?.defaultCurrency || 'EUR']
  ) || '€';
  const vehicles = useSelector(selectVehicles);
  const overview = useSelector(selectOverview);
  const monthlyCosts = useSelector(selectMonthlyCosts);
  const costsByDriver = useSelector(selectCostsByDriver);
  const costsByVehicle = useSelector(selectCostsByVehicle);
  const alerts = useSelector(selectAlerts);
  const loading = useSelector(selectStatsLoading);
  const [showDrivers, setShowDrivers] = useState(false);
  const [showVehicles, setShowVehicles] = useState(false);
  const [showFleets, setShowFleets] = useState(false);
  const [showMaintenance, setShowMaintenance] = useState(false);
  const [showGeofence, setShowGeofence] = useState(false);
  const [ecoScores, setEcoScores] = useState([]);
  const [financials, setFinancials] = useState({ revenue: 0, cost: 0, margin: 0, marginPct: 0 });

  useEffect(() => {
    if (token) {
      dispatch(fetchOverview(token));
      dispatch(fetchMonthlyCosts({ token, months: 6 }));
      dispatch(fetchCostsByDriver(token));
      dispatch(fetchCostsByVehicle(token));
      dispatch(fetchAlerts(token));
      // Fetch eco-driving scores
      fetch('/planner-api/eco-driving/scores', { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.json()).then(d => { if (Array.isArray(d)) setEcoScores(d); }).catch(() => {});
      // Fetch financial indicators
      fetch('/planner-api/transport-orders?page=1&limit=500&status=delivered', { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.json()).then(data => {
          const ots = data.data || data;
          if (!Array.isArray(ots)) return;
          const revenue = ots.reduce((s, o) => s + (Number(o.agreedPrice) || 0), 0);
          const fuelCost = (overview?.estimatedCostThisMonth?.fuel || 0);
          const tollCost = (overview?.estimatedCostThisMonth?.tolls || 0);
          const cost = fuelCost + tollCost;
          const margin = revenue - cost;
          const marginPct = revenue > 0 ? (margin / revenue) * 100 : 0;
          setFinancials({ revenue, cost, margin, marginPct });
        }).catch(() => {});
    }
  }, [dispatch, token]);

  const isLoading = loading.overview || loading.costs || loading.drivers || loading.vehicles || loading.alerts;

  // --- KPI computations ---
  const routesToday = overview?.routesToday ?? 0;
  const activeVehicles = vehicles.filter(v => {
    const spd = v.speed || v.position?.speed || 0;
    const ign = v.position?.attributes?.ignition;
    return spd > 0 || ign === true || ign === 'true';
  }).length;

  const pendingDeliveries = overview?.byStatus
    ? (overview.byStatus.planned || 0) + (overview.byStatus.in_progress || 0)
    : 0;

  const totalCostMonth = overview?.totalCostMonth ?? 0;
  const totalDistanceMonth = overview?.totalDistanceMonth ?? 0;
  const totalRoutesMonth = overview?.totalRoutesMonth ?? 0;
  const completedRoutesMonth = overview?.completedRoutesMonth ?? 0;
  const completionRate = totalRoutesMonth > 0 ? ((completedRoutesMonth / totalRoutesMonth) * 100) : 0;

  // Comparison percentages (current vs last month)
  const routesChange = overview?.routesChange ?? null;
  const costChange = overview?.costChange ?? null;
  const distanceChange = overview?.distanceChange ?? null;
  const completionChange = overview?.completionChange ?? null;

  // --- BarChart data from byStatus ---
  const statusBarData = useMemo(() => {
    const bs = overview?.byStatus || {};
    const t = (pt, en) => (lang === 'pt' ? pt : en);
    return [
      { name: t('Rascunho', 'Draft'), value: bs.draft || 0, color: '#94a3b8' },
      { name: t('Planeada', 'Planned'), value: bs.planned || 0, color: '#3b82f6' },
      { name: t('Em Trânsito', 'In Transit'), value: bs.in_progress || 0, color: '#f59e0b' },
      { name: t('Concluída', 'Completed'), value: bs.completed || 0, color: '#22c55e' },
      { name: t('Cancelada', 'Cancelled'), value: bs.cancelled || 0, color: '#ef4444' },
    ];
  }, [overview, lang]);

  // --- AreaChart data ---
  const areaData = useMemo(() => {
    if (!monthlyCosts || !monthlyCosts.length) return [];
    return monthlyCosts.map(mc => ({
      month: formatMonth(mc.month),
      fuel: mc.fuel ?? mc.fuelCost ?? 0,
      tolls: mc.tolls ?? mc.tollCost ?? 0,
    }));
  }, [monthlyCosts]);

  // --- Recent routes ---
  const recentRoutes = overview?.recentRoutes || [];

  if (isLoading && !overview) {
    return (
      <Box className={classes.root}>
        <Box className={classes.loadingWrap}>
          <CircularProgress size={40} style={{ color: '#3b82f6' }} />
        </Box>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      {/* Header */}
      <Box className={classes.header} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <IconButton size="small" onClick={() => dispatch(setActiveView('planner'))} style={{ color: '#64748b' }}>
          <ArrowBackIcon style={{ fontSize: 18 }} />
        </IconButton>
        <Typography className={classes.title}>Dashboard</Typography>
        <Typography className={classes.subtitle}>
          {new Date().toLocaleDateString('pt-PT', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        </Typography>
      </Box>

      {/* Section: Visão Geral */}
      <Typography className={classes.sectionHeader}>{lang === 'pt' ? 'Visão Geral' : 'Overview'}</Typography>

      {/* Row 1 -- KPI Cards */}
      <Box className={classes.kpiGrid}>
        <KpiCard
          lang={lang}
          icon={DirectionsIcon} color="#3b82f6"
          value={routesToday} label="Rotas Hoje"
          change={routesChange}
        />
        <KpiCard
          lang={lang}
          icon={LocalShippingIcon} color="#22c55e"
          value={`${activeVehicles}/${vehicles.length}`} label="Veiculos Ativos"
          change={null}
        />
        <KpiCard
          lang={lang}
          icon={HourglassEmptyIcon} color="#f59e0b"
          value={pendingDeliveries} label="Entregas Pendentes"
          change={null}
        />
        {/* Custo Total Mes removed — financial indicators hidden */}
        <KpiCard
          lang={lang}
          icon={MapIcon} color="#06b6d4"
          value={`${totalDistanceMonth.toFixed(0)} km`} label="Distancia Total Mes"
          change={distanceChange}
        />
        <KpiCard
          lang={lang}
          icon={CheckCircleIcon} color="#10b981"
          value={`${completionRate.toFixed(0)}%`} label="Taxa de Conclusao"
          change={completionChange}
        />
      </Box>

      {/* Section: Analise Operacional */}
      <Typography className={classes.sectionHeader}>{lang === 'pt' ? 'Análise Operacional' : 'Operations Analysis'}</Typography>

      {/* Row 2 -- Charts */}
      <Box className={classes.row2}>
        {/* Rotas por Estado -- Horizontal BarChart */}
        <Box className={classes.card}>
          <Typography className={classes.sectionTitle}>{lang === 'pt' ? 'Rotas por Estado' : 'Routes by Status'}</Typography>
          <Box style={{ width: '100%', height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={statusBarData} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                <defs>
                  <linearGradient id="gradDraft" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#94a3b8" stopOpacity={0.7} />
                    <stop offset="100%" stopColor="#94a3b8" stopOpacity={1} />
                  </linearGradient>
                  <linearGradient id="gradPlanned" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#60a5fa" stopOpacity={0.7} />
                    <stop offset="100%" stopColor="#3b82f6" stopOpacity={1} />
                  </linearGradient>
                  <linearGradient id="gradInProgress" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#fbbf24" stopOpacity={0.7} />
                    <stop offset="100%" stopColor="#f59e0b" stopOpacity={1} />
                  </linearGradient>
                  <linearGradient id="gradCompleted" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#4ade80" stopOpacity={0.7} />
                    <stop offset="100%" stopColor="#22c55e" stopOpacity={1} />
                  </linearGradient>
                  <linearGradient id="gradCancelled" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#f87171" stopOpacity={0.7} />
                    <stop offset="100%" stopColor="#ef4444" stopOpacity={1} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={{ stroke: '#e2e8f0' }} tickLine={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 12, fill: '#334155', fontWeight: 500 }} width={80} axisLine={false} tickLine={false} />
                <Tooltip
                  content={<CustomTooltip />}
                  cursor={{ fill: 'rgba(0,0,0,0.03)' }}
                />
                <Bar dataKey="value" radius={[0, 6, 6, 0]} barSize={26}>
                  {statusBarData.map((entry, idx) => {
                    const gradIds = ['gradDraft', 'gradPlanned', 'gradInProgress', 'gradCompleted', 'gradCancelled'];
                    return <Cell key={idx} fill={`url(#${gradIds[idx]})`} />;
                  })}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Box>

        {/* Custos por Mes -- AreaChart. Gateado pelo módulo `fleet_costs`
            (antes era pela variante, o que nem sequer funcionava aqui: a
            variante fuel nunca chega a abrir este dashboard). */}
        <Box className={classes.card} style={hasFleetCosts ? undefined : { display: 'none' }}>
          <Typography className={classes.sectionTitle}>{lang === 'pt' ? 'Custos por Mês' : 'Costs by Month'}</Typography>
          <Box style={{ width: '100%', height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={areaData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                <defs>
                  <linearGradient id="gradFuel" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gradTolls" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#94a3b8', fontWeight: 500 }} axisLine={{ stroke: '#e2e8f0' }} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} tickFormatter={(v) => `${v}${currencySymbol}`} />
                <Tooltip
                  content={
                    <CustomTooltip
                      formatter={(value, name) => `${Number(value).toFixed(2)}${currencySymbol}`}
                    />
                  }
                />
                <Legend
                  formatter={(val) => val === 'fuel'
                    ? (lang === 'pt' ? 'Combustível' : 'Fuel')
                    : (lang === 'pt' ? 'Portagens' : 'Tolls')}
                  wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
                />
                <Area
                  type="monotone" dataKey="fuel" name={lang === 'pt' ? 'Combustível' : 'Fuel'}
                  stroke="#3b82f6" strokeWidth={2.5} fill="url(#gradFuel)"
                  dot={{ r: 4, fill: '#fff', stroke: '#3b82f6', strokeWidth: 2 }}
                  activeDot={{ r: 6, fill: '#3b82f6', stroke: '#fff', strokeWidth: 2 }}
                />
                <Area
                  type="monotone" dataKey="tolls" name={lang === 'pt' ? 'Portagens' : 'Tolls'}
                  stroke="#f59e0b" strokeWidth={2.5} fill="url(#gradTolls)"
                  dot={{ r: 4, fill: '#fff', stroke: '#f59e0b', strokeWidth: 2 }}
                  activeDot={{ r: 6, fill: '#f59e0b', stroke: '#fff', strokeWidth: 2 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </Box>
        </Box>
      </Box>

      {/* Section: Performance */}
      <Typography className={classes.sectionHeader}>{lang === 'pt' ? 'Desempenho' : 'Performance'}</Typography>

      {/* Row 3 -- Tables */}
      <Box className={classes.row2}>
        {/* Custos por Motorista */}
        <Box className={classes.card}>
          <Typography className={classes.sectionTitle}>{lang === 'pt' ? 'Custos por Motorista' : 'Costs by Driver'}</Typography>
          <Box style={{ overflowX: 'auto' }}>
            <SortableTable
              defaultSort="totalDistance"
              columns={[
                { key: 'driverName', label: lang === 'pt' ? 'Motorista' : 'Driver' },
                { key: 'routeCount', label: lang === 'pt' ? 'Rotas' : 'Routes', render: (v) => v ?? 0 },
                { key: 'totalDistance', label: lang === 'pt' ? 'Distância (km)' : 'Distance (km)', render: (v) => (v ?? 0).toFixed(0) },
                { key: 'avgDistance', label: lang === 'pt' ? 'Média (km)' : 'Average (km)', render: (v) => (v ?? 0).toFixed(1) },
              ]}
              data={costsByDriver}
              lang={lang}
            />
          </Box>
        </Box>

        {/* Custos por Veiculo */}
        <Box className={classes.card}>
          <Typography className={classes.sectionTitle}>{lang === 'pt' ? 'Custos por Veículo' : 'Costs by Vehicle'}</Typography>
          <Box style={{ overflowX: 'auto' }}>
            <SortableTable
              defaultSort="totalDistance"
              columns={[
                { key: 'plate', label: lang === 'pt' ? 'Matrícula' : 'Plate' },
                { key: 'vehicleType', label: lang === 'pt' ? 'Tipo' : 'Type' },
                { key: 'routeCount', label: lang === 'pt' ? 'Rotas' : 'Routes', render: (v) => v ?? 0 },
                { key: 'totalDistance', label: lang === 'pt' ? 'Distância (km)' : 'Distance (km)', render: (v) => (v ?? 0).toFixed(0) },
              ]}
              data={costsByVehicle}
              lang={lang}
            />
          </Box>
        </Box>
      </Box>

      {/* Financial indicators removed per client request */}
      {false && financials.revenue > 0 && (
        <>
          <Typography className={classes.sectionHeader}>{lang === 'pt' ? 'Indicadores Financeiros' : 'Financial Indicators'}</Typography>
          <Box className={classes.kpiGrid} style={{ marginBottom: 20 }}>
            {[
              { label: lang === 'pt' ? 'Receita' : 'Revenue', value: `${financials.revenue.toFixed(0)}${currencySymbol}`, color: '#22c55e', change: null },
              { label: lang === 'pt' ? 'Custo Estimado' : 'Estimated Cost', value: `${financials.cost.toFixed(0)}${currencySymbol}`, color: '#ef4444', change: null },
              { label: lang === 'pt' ? 'Margem' : 'Margin', value: `${financials.margin.toFixed(0)}${currencySymbol}`, color: financials.margin >= 0 ? '#3b82f6' : '#ef4444', change: null },
              { label: lang === 'pt' ? 'Margem %' : 'Margin %', value: `${financials.marginPct.toFixed(1)}%`, color: '#8b5cf6', change: null },
            ].map((kpi, i) => (
              <Box key={i} className={classes.kpiCard} style={{ borderLeft: `3.5px solid ${kpi.color}` }}>
                <Typography style={{ fontSize: '0.62rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 600 }}>{kpi.label}</Typography>
                <Typography style={{ fontSize: '1.3rem', fontWeight: 800, color: '#1e293b' }}>{kpi.value}</Typography>
              </Box>
            ))}
          </Box>
        </>
      )}

      {/* Section: Eco-Driving */}
      {ecoScores.length > 0 && (
        <>
          <Typography className={classes.sectionHeader}>Eco-Driving Score</Typography>
          <Box style={{ display: 'flex', gap: 12, marginBottom: 20, overflowX: 'auto', paddingBottom: 4 }}>
            {ecoScores.slice(0, 8).map(d => {
              const color = d.score >= 80 ? '#22c55e' : d.score >= 60 ? '#f59e0b' : '#ef4444';
              const bg = d.score >= 80 ? '#dcfce7' : d.score >= 60 ? '#fef3c7' : '#fee2e2';
              return (
                <Box key={d.driverId} style={{
                  minWidth: 130, padding: '12px 14px', borderRadius: 12, background: '#fff',
                  border: '1px solid #e2e8f0', textAlign: 'center', flexShrink: 0,
                  transition: 'all 0.15s',
                }}>
                  <Box style={{
                    width: 44, height: 44, borderRadius: '50%', margin: '0 auto 8px',
                    background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Typography style={{ fontSize: '1rem', fontWeight: 800, color }}>{d.score}</Typography>
                  </Box>
                  <Typography style={{ fontSize: '0.68rem', fontWeight: 600, color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {d.driverName?.split(' ').slice(0, 2).join(' ')}
                  </Typography>
                  <Typography style={{ fontSize: '0.58rem', color: '#94a3b8' }}>
                    {d.totalKm} km · {d.totalRoutes} {lang === 'pt' ? 'rotas' : 'routes'}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </>
      )}

      {/* Section: Acções Rápidas */}
      <Typography className={classes.sectionHeader}>{lang === 'pt' ? 'Acções Rápidas' : 'Quick Actions'}</Typography>
      <Box style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 20 }}>
        {[
          { label: lang === 'pt' ? 'Gerir Motoristas' : 'Manage Drivers', icon: <PersonIcon style={{ fontSize: 18 }} />, color: '#3b82f6', onClick: () => setShowDrivers(true) },
          { label: lang === 'pt' ? 'Gerir Veículos' : 'Manage Vehicles', icon: <LocalShippingIcon style={{ fontSize: 18 }} />, color: '#22c55e', onClick: () => setShowVehicles(true) },
          { label: lang === 'pt' ? 'Gerir Frotas' : 'Manage Fleets', icon: <LocalShippingIcon style={{ fontSize: 18 }} />, color: '#8b5cf6', onClick: () => setShowFleets(true) },
          { label: lang === 'pt' ? 'Manutenção' : 'Maintenance', icon: <BuildIcon style={{ fontSize: 18 }} />, color: '#f59e0b', onClick: () => setShowMaintenance(true) },
          { label: lang === 'pt' ? 'Geofencing' : 'Geofencing', icon: <GpsFixedIcon style={{ fontSize: 18 }} />, color: '#06b6d4', onClick: () => setShowGeofence(true) },
        ].map((btn) => (
          <Box
            key={btn.label}
            onClick={btn.onClick}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '10px 18px', borderRadius: 12, cursor: 'pointer',
              background: '#fff', border: '1px solid #e2e8f0',
              boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
              transition: 'all 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
            onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)'; e.currentTarget.style.transform = 'none'; }}
          >
            <Box style={{ width: 34, height: 34, borderRadius: 8, background: `${btn.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: btn.color }}>
              {btn.icon}
            </Box>
            <Typography style={{ fontSize: '0.82rem', fontWeight: 600, color: '#1e293b' }}>{btn.label}</Typography>
          </Box>
        ))}
      </Box>

      {/* Dialogs */}
      <ManageDriversDialog open={showDrivers} onClose={() => setShowDrivers(false)} />
      <ManageVehiclesDialog open={showVehicles} onClose={() => setShowVehicles(false)} />
      <ManageFleetsDialog open={showFleets} onClose={() => setShowFleets(false)} lang={lang} />
      <MaintenanceDialog open={showMaintenance} onClose={() => setShowMaintenance(false)} lang={lang} />
      <GeofenceDialog open={showGeofence} onClose={() => setShowGeofence(false)} lang={lang} />

      {/* Section: Actividade */}
      <Typography className={classes.sectionHeader}>{lang === 'pt' ? 'Actividade' : 'Activity'}</Typography>

      {/* Row 4 -- Alerts + Recent Routes */}
      <Box className={classes.row2}>
        {/* Alertas */}
        <Box className={classes.card}>
          <Typography className={classes.sectionTitle}>
            <WarningIcon style={{ fontSize: 18, color: '#f59e0b' }} />
            {lang === 'pt' ? 'Alertas' : 'Alerts'}
          </Typography>
          {(!alerts || alerts.length === 0) ? (
            <Box style={{ textAlign: 'center', padding: '24px 0' }}>
              <CheckCircleIcon style={{ fontSize: 36, color: '#d1d5db' }} />
              <Typography style={{ fontSize: '0.78rem', color: '#94a3b8', marginTop: 8 }}>
                {lang === 'pt' ? 'Sem alertas ativos' : 'No active alerts'}
              </Typography>
            </Box>
          ) : (
            alerts.slice(0, 8).map((alert, i) => {
              const isHigh = alert.severity === 'high';
              const bgColor = isHigh ? 'rgba(239,68,68,0.10)' : 'rgba(245,158,11,0.10)';
              const iconColor = isHigh ? '#ef4444' : '#f59e0b';
              return (
                <Box key={i} className={classes.alertItem}>
                  <Box className={classes.alertIcon} style={{ background: bgColor }}>
                    {isHigh
                      ? <ErrorIcon style={{ fontSize: 16, color: iconColor }} />
                      : <WarningIcon style={{ fontSize: 16, color: iconColor }} />}
                  </Box>
                  <Box style={{ flex: 1, minWidth: 0 }}>
                    <Typography style={{ fontSize: '0.78rem', color: '#334155', fontWeight: 500 }}>
                      {alert.message || alert.text}
                    </Typography>
                    {alert.routeName && (
                      <Typography style={{ fontSize: '0.65rem', color: '#94a3b8' }}>
                        {lang === 'pt' ? 'Rota' : 'Route'}: {alert.routeName}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })
          )}
        </Box>

        {/* Rotas Recentes */}
        <Box className={classes.card}>
          <Typography className={classes.sectionTitle}>
            <DirectionsIcon style={{ fontSize: 18, color: '#3b82f6' }} />
            {lang === 'pt' ? 'Rotas Recentes' : 'Recent Routes'}
          </Typography>
          {(!recentRoutes || recentRoutes.length === 0) ? (
            <Box style={{ textAlign: 'center', padding: '24px 0' }}>
              <DirectionsIcon style={{ fontSize: 36, color: '#d1d5db' }} />
              <Typography style={{ fontSize: '0.78rem', color: '#94a3b8', marginTop: 8 }}>
                {lang === 'pt' ? 'Sem rotas recentes' : 'No recent routes'}
              </Typography>
            </Box>
          ) : (
            recentRoutes.slice(0, 5).map((r, i) => {
              const st = STATUS_COLORS[r.dbStatus || r.status] || STATUS_COLORS.draft;
              const dist = r.distance ? `${(r.distance / 1000).toFixed(0)} km` : '';
              return (
                <Box key={r.id || i} className={classes.recentRouteItem}>
                  <Box style={{ minWidth: 0 }}>
                    <Typography style={{ fontSize: '0.78rem', fontWeight: 600, color: '#1e293b' }}>
                      {r.name || (lang === 'pt' ? 'Sem nome' : 'No name')}
                    </Typography>
                    <Typography style={{ fontSize: '0.68rem', color: '#94a3b8' }}>
                      {dist}{r.driverName ? ` \u2022 ${r.driverName}` : ''}
                    </Typography>
                  </Box>
                  <span
                    className={classes.statusChip}
                    style={{ background: `${st.color}15`, color: st.color }}
                  >
                    {lang === 'pt' ? st.pt : st.en}
                  </span>
                </Box>
              );
            })
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default DashboardView;
